import React from 'react';
import { Button, Form } from 'react-bootstrap';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { type LoginAttributes } from '@soulhx/fs-common';
import { useDispatch } from 'react-redux';
import { updateToken, useLoginMutation } from '@soulhx/client-store';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { useErrorsAndWarnings } from '../../hooks/ErrorsAndWarnings.js';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query';

const schema = z.object({
  loginUsername: z.string().min(1, 'login.loginFields.username.invalid'),
  loginPassword: z.string().min(1, 'login.loginFields.password.invalid'),
});

type ValuesSchema = z.infer<typeof schema>;

export const Login = () => {
  const dispatch = useDispatch();
  const [login] = useLoginMutation();
  const { t } = useTranslation();
  const { AlertUI, addErrorMessage } = useErrorsAndWarnings();

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields, isSubmitting, isValid },
  } = useForm<ValuesSchema>({
    resolver: zodResolver(schema),
    mode: 'all',
    defaultValues: {
      loginUsername: '',
      loginPassword: '',
    },
  });

  const formSubmit = async (values: ValuesSchema) => {
    const credentials: LoginAttributes = {
      username: values.loginUsername.trim(),
      password: values.loginPassword.trim(),
    };

    try {
      const response = await login(credentials).unwrap();
      dispatch(updateToken(response.token));
    } catch (error) {
      const err = error as FetchBaseQueryError;
      if (err.status === 401) {
        addErrorMessage(t('login.loginError'));
      } else {
        addErrorMessage(t('login.submitError'));
      }
    }
  };

  return (
    <Form noValidate onSubmit={handleSubmit(formSubmit)}>
      <AlertUI />

      <Form.Group>
        <Form.Label>{t('login.loginFields.username.label')}</Form.Label>
        <Form.Control
          placeholder={t('login.loginFields.username.placeholder')}
          type="text"
          {...register('loginUsername')}
          isValid={!!touchedFields.loginUsername && !errors.loginUsername}
          isInvalid={!!touchedFields.loginUsername && !!errors.loginUsername}
          tabIndex={0}
        />
        <Form.Control.Feedback type="invalid">
          {errors.loginUsername?.message ? t(errors.loginUsername.message) : ''}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mt-2">
        <Form.Label>{t('login.loginFields.password.label')}</Form.Label>
        <Form.Control
          type="password"
          placeholder={t('login.loginFields.password.placeholder')}
          {...register('loginPassword')}
          isValid={!!touchedFields.loginPassword && !errors.loginPassword}
          isInvalid={!!touchedFields.loginPassword && !!errors.loginPassword}
          tabIndex={0}
        />
        <Form.Control.Feedback type="invalid">
          {errors.loginPassword?.message ? t(errors.loginPassword.message) : ''}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mt-2">
        <Button variant="primary" type="submit" disabled={isSubmitting || !isValid} tabIndex={0}>
          {t('login.loginBtn')}
        </Button>
      </Form.Group>

      <Form.Group className="mt-2">
        <NavLink aria-label={t('login.changePwd')} to="/changePwd" tabIndex={0}>
          {t('login.changePwd')}
        </NavLink>
      </Form.Group>
    </Form>
  );
};
